/**
 * API utility functions for all feature endpoints
 */

// Get session ID from sessionStorage or create one
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Generate image using unified endpoint
 */
export const generateFeature = async (featureType, formData, user = null) => {
  const headers = {};
  
  // Add session ID for rate limiting
  headers['X-Session-ID'] = getSessionId();
  
  // Add user ID if authenticated
  if (user && user.uid) {
    headers['X-User-ID'] = user.uid;
  }

  const response = await fetch(`${API_URL}/api/generate/${featureType}`, {
    method: 'POST',
    headers: headers,
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `Generation failed: ${response.statusText}`);
  }

  return data;
};

/**
 * Generate interior design
 */
export const generateInterior = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.roomType) formData.append('room_type', params.roomType);
  if (params.style) formData.append('style', params.style);
  if (params.colorPalette) formData.append('color_palette', params.colorPalette);
  if (params.customStylePrompt) {
    formData.append('custom_style_prompt', params.customStylePrompt);
  }

  return generateFeature('interior', formData, user);
};

/**
 * Generate exterior design
 */
export const generateExterior = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.buildingType) formData.append('building_type', params.buildingType);
  if (params.style) formData.append('style', params.style);
  if (params.colorPalette) formData.append('color_palette', params.colorPalette);
  if (params.customStylePrompt) {
    formData.append('custom_style_prompt', params.customStylePrompt);
  }

  return generateFeature('exterior', formData, user);
};

/**
 * Generate style transfer
 */
export const generateStyleTransfer = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.styleReferenceImage) {
    formData.append('style_reference_image', params.styleReferenceImage);
  } else if (params.styleReferenceUrl) {
    formData.append('style_reference_url', params.styleReferenceUrl);
  }
  
  if (params.style) formData.append('style', params.style);
  if (params.colorPalette) formData.append('color_palette', params.colorPalette);
  if (params.customStylePrompt) {
    formData.append('custom_style_prompt', params.customStylePrompt);
  }

  return generateFeature('style', formData, user);
};

/**
 * Generate paint
 */
export const generatePaint = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.paintMask) {
    formData.append('paint_mask', params.paintMask);
  }
  
  if (params.paintColor) {
    formData.append('paint_color', params.paintColor);
  }

  return generateFeature('paint', formData, user);
};

/**
 * Generate floor restyle
 */
export const generateFloor = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.floorMask) {
    formData.append('floor_mask', params.floorMask);
  }
  
  if (params.floorMaterial) {
    formData.append('floor_material', params.floorMaterial);
  }
  
  if (params.customFloorDescription) {
    formData.append('custom_floor_description', params.customFloorDescription);
  }
  
  if (params.floorColor) {
    formData.append('floor_color', params.floorColor);
  }

  return generateFeature('floor', formData, user);
};

/**
 * Generate replace object
 */
export const generateReplace = async (params, user = null) => {
  const formData = new FormData();
  
  if (params.roomImage) {
    formData.append('room_image', params.roomImage);
  } else if (params.exampleImageUrl) {
    formData.append('example_image_url', params.exampleImageUrl);
  }
  
  if (params.baseImageUrl) {
    formData.append('base_image_url', params.baseImageUrl);
  }
  
  if (params.objectMask) {
    formData.append('object_mask', params.objectMask);
  }
  
  if (params.replacementObjectImage) {
    formData.append('replacement_object_image', params.replacementObjectImage);
  } else if (params.replacementObjectUrl) {
    formData.append('replacement_object_url', params.replacementObjectUrl);
  } else if (params.replacementObjectDescription) {
    formData.append('replacement_object_description', params.replacementObjectDescription);
  }

  return generateFeature('replace', formData, user);
};

